import React from 'react'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { requestTvPageData } from '../utilities/tvPageData'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiLink } from '@react-icons/all-files/fi/FiLink'
import { FiTruck } from '@react-icons/all-files/fi/FiTruck'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products'
import { SHOW_POPUP } from '../store/slices/ui'
import { getUserGeo } from '../store/slices/user'
import { getWorkingOrder } from '../store/slices/entities/orders'
import { resolveShoppingMarket } from '../utilities/fulfillmentMarket'
import ProductList from '../components/ProductList'
import LandingCategories from '../components/LandingCategories'
import QuotePop from '../components/DeferredQuotePopup'
import PageShell from '../components/design-system/foundations/PageShell'
import ServiceHero from '../components/design-system/marketing/ServiceHero'
import TVRentalChooser from '../components/design-system/marketing/TVRentalChooser'
import VisualDecisionGuide from '../components/design-system/marketing/VisualDecisionGuide'
import ClosingActionBand from '../components/design-system/marketing/ClosingActionBand'
import EventShowcase from '../components/design-system/marketing/EventShowcase'
import ReviewProof from '../components/design-system/marketing/ReviewProof'
import RentalFAQV2 from '../components/design-system/marketing/RentalFAQV2'
import RelatedServices from '../components/design-system/marketing/RelatedServices'
import tvRentalHeroV2 from '../public/Pictures/categories-v2/tv-rental-hero-v2.jpg'
import { compactCatalogProducts } from '../utilities/catalogPublicPayload'
import { compactPublicEvents } from '../utilities/eventPublicPayload'
import { getMiamiTvCatalogs } from '../utilities/miamiTvCatalog'
import { tvQuoteContent, tvRentalQuestions } from '../utilities/tvRentalContent'
import { getVisualFamilyBreadcrumbs, toBreadcrumbJsonLdItems, toHeroBreadcrumbs } from '../utilities/visualFamilyBreadcrumbs'
import * as gtag from '../lib/gtag'

const title = 'TV Rental for Events in Miami & South Florida'
const description = 'Rent flat-screen TVs, smart TVs, monitors, touchscreens, stands, and multi-display systems for events in Miami, Fort Lauderdale, and South Florida.'
const canonical = 'https://miamisoundrental.com/tv-rental'

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [products, portfolio, reviewResponse] = await Promise.all([
    requestTvPageData({ url: `${SERVER_IP}/inventory/product?msr=true&cat=tvs`, required: true, validateData: Array.isArray, label: 'catalog for /tv-rental' }),
    // The same public equipment-linked proof endpoint used by product details.
    requestTvPageData({ url: `${SERVER_IP}/newsletterevent/product/5f3150cfe52cbd37797ad26a`, label: 'event proof for /tv-rental' }),
    requestTvPageData({ url: `${SERVER_IP}/google-review/public?page=%2Ftv-rental&limit=3`, label: 'reviews for /tv-rental' })
  ])
  store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products.data) })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })
  const reviewData = reviewResponse.data
  const tvReviews = reviewData?.reviews?.filter(review => /\b(tv|television|monitor|display|screen)s?\b/i.test(review.comment || '')) || []
  return {
    props: {
      tvEvents: compactPublicEvents(portfolio.data?._id ? [portfolio.data] : []),
      tvReviewData: tvReviews.length ? { ...reviewData, reviews: tvReviews } : null
    },
    revalidate: portfolio.usedFallback || reviewResponse.usedFallback ? 60 : 3600
  }
})

const TVRental = ({ tvEvents = [], tvReviewData }) => {
  const products = useSelector(getFilteredProducts)
  const catalogs = getMiamiTvCatalogs(products)
  const dispatch = useDispatch()
  const geo = useSelector(getUserGeo)
  const order = useSelector(getWorkingOrder)
  const market = resolveShoppingMarket({ geo, order, pathname: '/tv-rental' }) === 'Orlando'
    ? 'orlando' : 'miami_south_florida'
  const context = sourceSurface => ({ market, sourceSurface, serviceCategory: 'tv_rental' })
  const openQuote = sourceSurface => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders', ...context(sourceSurface) })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop {...tvQuoteContent} /> }))
  }
  const browseAction = sourceSurface => ({
    label: 'Browse TV Bundles', href: '#productsAnchor', icon: FiMonitor,
    conversionKey: 'tv-rental.hero.browse-tvs', channel: 'rental', salesPath: 'self_service',
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders', ...context(sourceSurface) })
  })
  const helpAction = sourceSurface => ({
    label: 'Get Help Planning TV Displays', icon: FiMessageCircle,
    conversionKey: 'tv-rental.hero.talk-to-expert', channel: 'quote', salesPath: 'managed',
    onClick: () => openQuote(sourceSurface)
  })
  const catalogEmptyState = (heading, kind) => ({
    heading,
    description: 'Ask our team to check the equipment options for your event and fulfillment needs.',
    action: { ...helpAction(`tv_rental:${kind}_empty_catalog`), conversionKey: `tv-rental.${kind}.empty.talk-to-expert` }
  })
  const breadcrumbs = getVisualFamilyBreadcrumbs('/tv-rental')
  const setupItems = [
    {
      icon: FiMonitor, title: 'Screen and support equipment',
      summary: 'Match the actual screen size to the audience, viewing distance, content and available space. Confirm the selected bundle’s stand or mount, screen resolution and included items.',
      points: ['Portrait signage needs a compatible vertical configuration.', 'Touchscreen content and source-device compatibility need checking.', 'Presenter confidence monitors have their own support configuration.']
    },
    {
      icon: FiLink, title: 'Source, connections and sound',
      summary: 'Confirm the source device, HDMI or USB-C connection, cable distance, power and content format. Adapters, sound and distribution equipment depend on the chosen setup.',
      points: ['Check included cables and remotes on the product page.', 'Several displays may need a signal-distribution plan.', 'Long or wireless signal paths should be checked in advance.']
    },
    {
      icon: FiTruck, title: 'Dates, venue and fulfillment',
      summary: 'Select dates and your fulfillment method to check the applicable warehouse, live availability and complete-order price. Pickup is available only for eligible products.',
      points: ['Some configurations require delivery and setup.', 'Confirm venue access, mounting and installation needs.', 'Exposed or bright outdoor conditions require a separate assessment.']
    }
  ]

  return <>
    <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
    <NextSeo title={title} description={description} canonical={canonical}
      twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: 'summary_large_image' }}
      openGraph={{ type: 'website', url: canonical, title, description,
        images: [{ url: 'https://miamisoundrental.com/Pictures/categories-v2/tv-rental-hero-v2.jpg', width: 1915, height: 821, alt: 'Large flat-screen TV on a professional freestanding event display in Miami' }],
        site_name: 'Miami Sound Rental' }}
    />
    <PageShell>
      <ServiceHero breadcrumbs={toHeroBreadcrumbs(breadcrumbs)} eyebrow='TV rental · Miami & Fort Lauderdale'
        title='TV Rental for Events in Miami'
        description='Compare complete TV display bundles for presentations, signage and events. Individual TVs, monitors and touchscreens are also available; check each product’s pickup, delivery and setup options.'
        image={{ src: tvRentalHeroV2, position: 'center' }}
        imageAlt='Large flat-screen TV on a professional freestanding event display in Miami'
        primaryAction={browseAction('tv_rental:hero')} secondaryAction={helpAction('tv_rental:hero')}
        stackActionsOnMobile processLabel='How TV rental works'
        steps={[
          { icon: 'search', title: 'Choose your display', description: 'Compare screens and complete configurations.' },
          { icon: 'calendar', title: 'Select dates', description: 'Tell us when and where you need it.' },
          { icon: 'truck', title: 'Check fulfillment', description: 'Pickup or delivery depends on the product.' },
          { icon: 'success', title: 'Confirm your setup', description: 'Check inclusions, connections and availability.' }
        ]}
      />
      <TVRentalChooser />
      <ProductList anchor listName='Complete TV Display Bundles' eyebrow='Screen and support configurations'
        description='Audience displays, portrait signage, interactive touchscreens and presenter confidence monitors. Each configuration lists its own included equipment. Rental prices are for the selected equipment; dates, fulfillment and the complete order determine the final price.'
        products={catalogs.bundles} showAssurances={false} impressionMode='visible' headerDensity='compact' sortBy={{ 'Screen Size': true }}
        emptyState={catalogEmptyState('No TV bundles to show for this selection.', 'bundles')}
        carousel={{ name: 'TV Rentals', url: canonical, description: 'Complete TV display bundle rentals' }}
      />
      <ClosingActionBand headingId='tv-planning-help' eyebrow='TV-specific planning' heading='Get Help Planning TV Displays'
        description='Ask about a complex single display or several screens: quantities, orientation, source devices, signal distribution, venue access, delivery and installation.'
        primaryAction={{ ...helpAction('tv_rental:planning'), conversionKey: 'tv-rental.planning.talk-to-expert' }}
        secondaryAction={{ ...browseAction('tv_rental:planning'), conversionKey: 'tv-rental.planning.browse-tvs' }}
      />
      <ProductList anchorId='individual-displays' listName='Individual TVs, Monitors and Touchscreens' eyebrow='Independent display rentals'
        description='Already have the support equipment? Choose an individual TV, monitor or touchscreen, then check its mounting, connections and pickup, delivery or installation requirements.'
        products={catalogs.displays} showAssurances={false} impressionMode='visible' headerDensity='compact' sortBy={{ 'Screen Size': true }}
        emptyState={catalogEmptyState('No individual displays to show for this selection.', 'displays')}
      />
      <ProductList anchorId='tv-accessories' listName='TV Stands and Accessories' eyebrow='Supporting equipment'
        description='These items are not complete TV setups. Confirm compatibility and what your chosen display includes before adding a stand, bracket, remote or other support equipment.'
        products={catalogs.accessories} showAssurances={false} impressionMode='visible' headerDensity='compact'
        emptyState={catalogEmptyState('No TV accessories to show for this selection.', 'accessories')}
      />
      {catalogs.other.length > 0 && <ProductList listName='More TV Equipment'
        description='Check each product’s specifications, included equipment and fulfillment requirements.'
        products={catalogs.other} showAssurances={false} impressionMode='visible' headerDensity='compact'
      />}
      <VisualDecisionGuide id='tv-setup-guide' eyebrow='Before choosing dates' heading='Check Your Display Setup'
        description='Confirm the selected configuration’s equipment and the event’s connection, sound and fulfillment needs.'
        density='compact' items={setupItems}
      />
      <EventShowcase events={tvEvents} layout='featured' heading='TV Displays at Real Events' sectionId='tv-event-proof' eyebrow='Event examples'
        description='These public event records are linked to the 65-inch TV-on-truss configuration. Open the case study to see the wider event scope.'
      />
      {tvReviewData && <ReviewProof initialData={tvReviewData} layout='featured' enableLiveData={false} pagePath='/tv-rental' sectionId='tv-client-reviews'
        eyebrow='Customer feedback' heading='Reviews About TV Display Setups'
        description='Customer feedback about TV display setups. The rating and review count describe the company’s overall Google profile.'
      />}
      <RentalFAQV2 questions={tvRentalQuestions} headingId='tv-rental-faq-title' />
      <RelatedServices headingId='tv-display-alternatives' eyebrow='Compare display formats' heading='TV, Projector or LED Video Wall?'
        description='These are alternative display technologies. Choose by image size, content, venue light, viewing distance and installation requirements.'
        additionalServices={[
          { h1: 'Projector and Screen Rental', url: '/projector-rental', miniText: 'Compare projection brightness, screen size and light-control requirements.' },
          { h1: 'LED Video Wall Rental', url: '/led-video-wall-rental', miniText: 'Explore modular panels for larger formats and planned installations.' },
          { h1: 'Visual Equipment Options', url: '/visuals', miniText: 'Compare the visual-equipment categories before choosing a format.' }
        ]}
      />
      <ClosingActionBand headingId='tv-closing-actions' eyebrow='Choose your next step' heading='Choose Your TV Display'
        description='Browse complete bundles for your Miami or South Florida event, or ask for help with the configuration.'
        primaryAction={browseAction('tv_rental:closing')} secondaryAction={helpAction('tv_rental:closing')}
      />
      <LandingCategories ui='v2' loading='lazy' category='visuals' options white title
        eyebrow='Other event equipment'
        heading='Planning More Than TV Displays?'
        description='Explore the specialist rental categories for sound, lighting, staging and other event needs.'
      />
    </PageShell>
  </>
}

export default TVRental
